import React, { useEffect, useState } from 'react';
import './EnergySection.css';
import dominantEnergyMeanings from '../data/dominantEnergyMeanings';
import getEnergyStyle from '../utils/getEnergyStyle';
import getNumberStyle from '../utils/getNumberStyle';
import comboEnergyMeanings from '../data/comboEnergyMeanings';
import ImportantNotices from './ImportantNotices';
import { auth, checkAuthorizedEmail, getUserData } from '../firebase';
import UpgradeModal from './UpgradeModal';
import energyMeanings from '../data/energyMeanings';
import { energyTypes } from '../data/energyTypes';
import VIPPricing from './VIPPricing';

const EnergySection = ({
  energyPairs,
  dominantEnergy,
  phoneNumber,
  getHexagramStyle,
  onEnergyData,
}) => {
  const [notifications, setNotifications] = useState([]);
  const [ghiChu, setGhiChu] = useState([]);
  const [warning, setWarning] = useState('');
  const [showCombinedPairs, setShowCombinedPairs] = useState(false);
  const [showDominantMeaning, setShowDominantMeaning] = useState(false);
  const [showVIPPricing, setShowVIPPricing] = useState(false);
  const [userRole, setUserRole] = useState('regular');

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async user => {
      if (user) {
        const userData = await getUserData(user.uid);
        setShowCombinedPairs(userData?.role === 'vip' || userData?.role === 'admin');
        setUserRole(userData?.role || 'regular');
      } else {
        setShowCombinedPairs(false);
        setUserRole('regular');
      }
    });
    return () => unsubscribe();
  }, []);

  const combinedPairs = [];
  if (energyPairs && energyPairs.length > 1) {
    for (let i = 0; i < energyPairs.length - 1; i++) {
      const current = energyPairs[i];
      const next = energyPairs[i + 1];
      
      // Nếu là cặp cuối cùng
      if (i === energyPairs.length - 2) {
        // Lấy đến hết số điện thoại để bao gồm cả các số 0,5 liền kề ở cuối
        const combinedNumber = phoneNumber.substring(parseInt(current.position.split('-')[0]) - 1);
        
        const fullCurrentEnergyName = current.energy.split(' ').slice(0, 2).join(' ');
        const fullNextEnergyName = next.energy.split(' ').slice(0, 2).join(' ');
        const combinedEnergyName = `${fullCurrentEnergyName} + ${fullNextEnergyName}`;
        
        const meaning = comboEnergyMeanings[combinedEnergyName]?.description || "Không có thông tin chi tiết.";

        combinedPairs.push({
          number: combinedNumber,
          energy: combinedEnergyName,
          meaning: meaning
        });
      } else {
        // Với các cặp không phải cuối, giữ nguyên cách tính cũ
        let currentNumber = current.number;
        let nextNumber = next.number;
        let combinedNumber = currentNumber + nextNumber;
        let shouldSimplify = false;
        
        // Kiểm tra từng cặp số liên tiếp trong số kết hợp
        for (let j = 1; j < combinedNumber.length - 2; j++) {
          if (
            combinedNumber[j] === combinedNumber[j + 1] && // Hai số giống nhau
            j !== 0 && // Không phải số đầu
            j + 1 !== combinedNumber.length - 1 // Không phải số cuối
          ) {
            // Rút gọn bằng cách chỉ giữ lại một số
            combinedNumber = 
              combinedNumber.substring(0, j) + 
              combinedNumber[j] + 
              combinedNumber.substring(j + 2);
            shouldSimplify = true;
            break;
          }
        }

        const fullCurrentEnergyName = current.energy.split(' ').slice(0, 2).join(' ');
        const fullNextEnergyName = next.energy.split(' ').slice(0, 2).join(' ');
        const combinedEnergyName = `${fullCurrentEnergyName} + ${fullNextEnergyName}`;
        
        const meaning = comboEnergyMeanings[combinedEnergyName]?.description || "Không có thông tin chi tiết.";

        combinedPairs.push({
          number: combinedNumber,
          energy: combinedEnergyName,
          meaning: meaning
        });
      }
    }
  }

  // Rest of the component remains the same...
  useEffect(() => {
    if (energyPairs) {
      const filteredItems = energyPairs
        .filter(item => String(item.number).includes('0') || String(item.number).includes('5'))
        .map(item => ({
          code: item.number,
          description: item.energy,
          meaning: energyMeanings[item.energy]?.description || "Không có thông tin chi tiết.",
        }));
      setNotifications(filteredItems);
    }
  }, [energyPairs]);

  const countZeros = (phoneNumber) => {
    return phoneNumber.split('').filter(digit => digit === '0').length;
  };

  const checkLastDigits = (phoneNumber) => {
    if (!phoneNumber) return { isValid: false, endingDigits: '' };
    
    // Tìm tổ hợp số 0,5 liền nhau từ cuối lên
    let endIndex = phoneNumber.length;
    let startIndex = endIndex;
    
    // Duyệt từ cuối lên đầu để tìm điểm bắt đầu của tổ hợp
    for (let i = phoneNumber.length - 1; i >= 0; i--) {
      const currentDigit = phoneNumber[i];
      if (currentDigit === '0' || currentDigit === '5') {
        startIndex = i;
      } else {
        break;
      }
    }
    
    // Trả về tổ hợp nếu có ít nhất một số 0
    if (startIndex < endIndex) {
      const endingDigits = phoneNumber.slice(startIndex, endIndex);
      if (endingDigits.includes('0')) {
        return { isValid: true, endingDigits };
      }
    }
    
    return { isValid: false, endingDigits: '' };
  };

  useEffect(() => {
    if (energyPairs) {
      const hasSinhKhi = energyPairs.some(item => item.energy.includes("Sinh khí"));
      const hasThienY = energyPairs.some(item => item.energy.includes("Thiên y"));
      const hasDienNien = energyPairs.some(item => item.energy.includes("Diên niên"));
      const zeroCount = countZeros(phoneNumber);
      const { isValid: hasEndingZeroOrZeroFive, endingDigits } = checkLastDigits(phoneNumber);

      const newWarnings = [];

      if (!hasSinhKhi) {
        newWarnings.push({
          title: 'Số này không có Sinh khí:',
          description: 'Không có quý nhân giúp đỡ, mọi việc tựa vào bản thân mình, kiếm tiền vất vả, cuộc sống rất mệt mỏi'
        });
      }
      if (!hasThienY) {
        newWarnings.push({
          title: 'Số này không có Thiên y:',
          description: 'Tài chính không lý tưởng, kiếm tiền vất vả và hôn nhân không hòa hợp'
        });
      }
      if (!hasDienNien) {
        newWarnings.push({
          title: 'Số này không có Diên niên:',
          description: 'Năng lực hạn chế, không chịu được áp lực cao'
        });
      }
      if (zeroCount >= 4) {
        newWarnings.push({
          title: 'Số này có nhiều 0:',
          description: '0 đại diện cho việc tiêu hao nguyên khí cơ thể, dễ mệt mỏi, thậm chí phẫu thuật mổ bụng, vất vả, thành công người khác, thích tu luyện.'
        });
      }
      if (hasEndingZeroOrZeroFive) {
        const warningText = {
          title: `⚠️ Số cuối của số điện thoại là ${endingDigits}:`,
          subtitle: 'Tượng trưng cho sự hao tổn, gồm có:',
          points: [
            'Tài chính (dù trước đây có bao nhiêu tài sản) cũng sẽ dần giảm đi',
            'Sự nghiệp (dù trước đây sự nghiệp có thành công) cũng sẽ dần thu hẹp giảm đi',
            'Cơ thể (dù cơ thể có tốt đến đâu) cũng sẽ tiêu hao nguyên khí và sức khỏe giảm sút',
            'Gia đình (dù gia đình có hòa thuận) bầu không khí cũng sẽ giảm đi và trở nên lạnh lùng và biến chuyển',
            `Số điện thoại kết thúc bằng ${endingDigits}, sử dụng trên 3 năm, ít nhất cũng có 1 dự đoán phía trên thành hiện thực.`
          ]
        };
        newWarnings.push({
          title: warningText.title,
          description: `${warningText.subtitle}\n${warningText.points.map(point => `${point}`).join('. ')}`
        });
      }
      setGhiChu(newWarnings);
    }
  }, [energyPairs, phoneNumber]);

  useEffect(() => {
    if (energyPairs && energyPairs.length > 0) {
      const lastEnergyItem = energyPairs[energyPairs.length - 1];
      const lastEnergy = lastEnergyItem ? lastEnergyItem.energy : null;
      const lastNumber = lastEnergyItem ? lastEnergyItem.number : null;
      const badEndings = ["Tuyệt mệnh", "Họa hại", "Ngũ quỷ", "Lục sát", "+0", "kẹp 0"];

      if (lastEnergy && badEndings.some(ending => lastEnergy.includes(ending))) {
        const energyMeaning = energyMeanings[lastEnergy]?.description || "Không có thông tin chi tiết.";
        
        // Tách nội dung thành tiêu đề phụ và các điểm
        const lines = energyMeaning.split('\n').filter(line => line.trim());
        const subtitle = lines[0]; // Dòng đầu tiên là tiêu đề phụ
        const points = lines.slice(1) // Các dòng còn lại là các điểm
          .map(point => {
            // Remove existing bullet points and dashes
            return point.replace(/^[-•*]\s*/, '').trim();
          });

        const warningText = {
          title: `⚠️ Số đuôi ${lastNumber}: Mang năng lượng ${lastEnergy}`,
          subtitle: subtitle,
          points: points
        };
        
        setWarning(warningText);
      } else {
        setWarning('');
      }
    } else {
      setWarning('');
    }
  }, [energyPairs]);

  useEffect(() => {
    if (onEnergyData) {
      onEnergyData(dominantEnergy, combinedPairs, notifications, ghiChu, warning, energyPairs);
    }
  }, [dominantEnergy, combinedPairs, notifications, ghiChu, warning, energyPairs, onEnergyData]);

  const getEnergyType = (number) => {
    if (!number) return '';
    const pairs = Object.keys(energyTypes);
    for (let pair of pairs) {
      if (pair.includes(number)) {
        return energyTypes[pair];
      }
    }
    return '';
  };

  const lastNumber = phoneNumber[phoneNumber.length - 1];
  const lastEnergy = getEnergyType(lastNumber);
  const lastEnergyMeaning = energyMeanings[lastEnergy]?.description || "Không có thông tin chi tiết.";

  return (
    <section className="energy-section">
      <h2 className="section-title">⚛️ Thông Tin Năng Lượng Số {phoneNumber}</h2>
      <div className="energy-pairs-row">
        {energyPairs.map((pair, index) => (
          <div key={index} className="energy-pair-card" style={{ width: `${100 / energyPairs.length}%` }}>
            <div className="energy-number-row">
              <h3 style={getNumberStyle(pair.number)}>{pair.number}</h3>
            </div>
            <div className="separator-line"></div>
            <div className="energy-name-row" style={getEnergyStyle(pair.energy)}>
              <p style={{ color: '#000' }}>{pair.energy}</p>
            </div>
          </div>
        ))}
      </div>
      {dominantEnergy && (
        <div className="dominant-energy-card">
          <div className="energy-number-row">
            <h3 style={getNumberStyle(dominantEnergy)}>Năng Lượng Chủ Đạo</h3>
          </div>
          <div className="separator-line"></div>
          <div className="energy-name-row" style={{...getEnergyStyle(dominantEnergy), flexDirection: 'column'}}>
            <p style={{ color: '#000' }}>{dominantEnergy}</p>
            {showDominantMeaning && dominantEnergy && dominantEnergyMeanings[dominantEnergy] && (
              <div className="dominant-meaning">
                {dominantEnergyMeanings[dominantEnergy].description.split('\n').map((line, index) => (
                  <p key={index} className={line.trim().startsWith('---') ? 'sub-bullet' : ''}>
                    {line.trim().startsWith('---') ? line.substring(3).trim() : line}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {userRole === 'admin' && (
        <div className="combined-pairs-section">
          <div className="energy-title">
            <h2>🔢 Chi tiết Tổ hợp Năng lượng số</h2>
          </div>
          {showCombinedPairs && combinedPairs.length > 0 && (
            <div className="combined-pairs-content">
              <div className="table-container">
                <table className="table-border">
                  <thead>
                    <tr>
                      <th style={{ width: '15%' }}>Tổ hợp số</th>
                      <th style={{ width: '15%' }}>Tên tổ hợp</th>
                      <th style={{ width: '70%' }}>Ý nghĩa</th>
                    </tr>
                  </thead>
                  <tbody>
                    {combinedPairs.map((pair, index) => (
                      <tr key={index} style={{ width: '100%' }}>
                        <td style={{ width: '15%' }}>{pair.number}</td>
                        <td style={{ width: '15%' }}>{pair.energy}</td>
                        <td style={{ width: '70%' }} className="combined-meaning-column">
                          {pair.meaning && (
                            <ul className="list-disc list-inside">
                              {pair.meaning.split('\n').map((line, lineIndex) => (
                                <li key={lineIndex} className={line.startsWith('-') ? 'sub-bullet' : ''}>
                                  {line.startsWith('-') ? line.substring(1).trim() : line}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
      <ImportantNotices
        notifications={notifications}
        ghiChu={ghiChu}
        warning={warning}
      />
      {showVIPPricing && <VIPPricing onClose={() => setShowVIPPricing(false)} />}
    </section>
  );
};

export default EnergySection;
